import React from 'react';
import { Helmet } from 'react-helmet';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import {
  GraduationCap,
  Bot,
  ShieldCheck,
  CheckCircle2,
  ArrowUpRight,
  Sparkles,
  Users,
  Building2,
  Monitor,
  Clock,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SafeJsonLd from '@/components/SafeJsonLd';
import { useLanguage } from '@/contexts/LanguageContext';
import ServiceFAQ, { FAQItem } from '@/components/service/ServiceFAQ';
import ServiceCrossSell from '@/components/service/ServiceCrossSell';
import ServiceBreadcrumb from '@/components/service/ServiceBreadcrumb';

const ServicesTraining: React.FC = () => {
  const { t, language } = useLanguage();
  const isPT = language === 'pt';

  const title = isPT
    ? 'Formações em IA e Higiene Digital | Spawncore'
    : 'AI Training & Digital Hygiene Workshops | Spawncore';

  const description = isPT
    ? 'Workshops práticos em IA aplicada ao trabalho e em Higiene Digital — cibersegurança aplicada ao utilizador. Para equipas de pequenas empresas, sem jargão técnico.'
    : 'Hands-on workshops in workplace AI and Digital Hygiene — cybersecurity basics for everyday users. For small business teams, no jargon.';

  const modules = isPT ? [
    {
      id: 'ai',
      Icon: Bot,
      tag: 'Formação · IA Aplicada',
      title: 'Formações IA',
      tagline: 'Use IA todos os dias. Sem ser engenheiro.',
      description: 'Aprenda a usar ChatGPT, Claude e ferramentas no-code para escrever, resumir, pesquisar, criar imagens e automatizar pequenas tarefas. Foco em casos reais do seu trabalho — não em teoria.',
      coverage: [
        'Fundamentos de IA generativa (em linguagem clara)',
        'Prompts eficazes para escrever emails, propostas, posts',
        'Resumir reuniões, transcrever áudio, extrair dados de PDFs',
        'Criar imagens e materiais visuais com IA',
        'Usar Claude/ChatGPT no telemóvel e no portátil',
        'Privacidade: o que NÃO meter em ferramentas IA',
        'Ferramentas no-code: Make, Zapier, automações simples',
        'Casos práticos do seu setor',
      ],
      format: 'Online ou presencial · 3h ou 6h · até 15 pessoas',
      price: 'Desde €350',
      accent: 'lime' as const,
    },
    {
      id: 'hygiene',
      Icon: ShieldCheck,
      tag: 'Formação · Cibersegurança aplicada',
      title: 'Higiene Digital',
      tagline: 'O essencial para se proteger online.',
      description: 'Cibersegurança básica para utilizadores comuns: a sua equipa, a sua família, você. Sem termos técnicos — só hábitos práticos que evitam 95% dos problemas reais com phishing, palavras-passe e burlas online.',
      coverage: [
        'Palavras-passe seguras e gestores de passwords',
        'Autenticação em dois passos (2FA) — porquê e como',
        'Reconhecer phishing e golpes por email/SMS',
        'Higiene em redes sociais e privacidade',
        'WhatsApp, MB Way e burlas comuns em Portugal',
        'Backups e recuperação de dados',
        'Segurança no teletrabalho e WiFi público',
        'O que fazer se for vítima de fraude online',
      ],
      format: 'Online ou presencial · 2h ou 4h · até 20 pessoas',
      price: 'Desde €250',
      accent: 'primary' as const,
    },
  ] : [
    {
      id: 'ai',
      Icon: Bot,
      tag: 'Workshop · Applied AI',
      title: 'AI Training',
      tagline: 'Use AI every day. Without being an engineer.',
      description: 'Learn to use ChatGPT, Claude and no-code tools to write, summarise, research, create images and automate small tasks. Focus on real cases from your work — not theory.',
      coverage: [
        'Generative AI fundamentals (plain language)',
        'Effective prompts for emails, proposals, posts',
        'Summarise meetings, transcribe audio, extract data from PDFs',
        'Create images and visual materials with AI',
        'Using Claude/ChatGPT on phone and laptop',
        'Privacy: what NOT to put into AI tools',
        'No-code tools: Make, Zapier, simple automations',
        'Sector-specific practical cases',
      ],
      format: 'Online or in-person · 3h or 6h · up to 15 people',
      price: 'From €350',
      accent: 'lime' as const,
    },
    {
      id: 'hygiene',
      Icon: ShieldCheck,
      tag: 'Workshop · Applied cybersecurity',
      title: 'Digital Hygiene',
      tagline: 'The essentials to stay safe online.',
      description: 'Basic cybersecurity for regular users: your team, your family, you. No technical jargon — just practical habits that prevent 95% of real problems with phishing, passwords and online scams.',
      coverage: [
        'Strong passwords and password managers',
        'Two-factor authentication (2FA) — why and how',
        'Recognising phishing and email/SMS scams',
        'Social media hygiene and privacy',
        'WhatsApp, mobile payments and common scams',
        'Backups and data recovery',
        'Remote work and public WiFi safety',
        'What to do if you fall for online fraud',
      ],
      format: 'Online or in-person · 2h or 4h · up to 20 people',
      price: 'From €250',
      accent: 'primary' as const,
    },
  ];

  const targetGroups = isPT ? [
    { Icon: Building2, title: 'Pequenas empresas', desc: 'A equipa inteira fica autónoma — usa IA bem e sabe defender-se.' },
    { Icon: Users,     title: 'Equipas comerciais', desc: 'Vendedores que escrevem propostas em metade do tempo, em segurança.' },
    { Icon: Monitor,   title: 'Profissionais liberais', desc: 'Médicos, advogados, contabilistas — IA aplicada com cuidado RGPD.' },
    { Icon: GraduationCap, title: 'Famílias & seniores', desc: 'Sessões específicas para proteger pais, avós e adolescentes online.' },
  ] : [
    { Icon: Building2, title: 'Small businesses',     desc: 'The whole team becomes self-sufficient — uses AI well and stays safe.' },
    { Icon: Users,     title: 'Sales teams',          desc: 'Sales reps who write proposals in half the time, securely.' },
    { Icon: Monitor,   title: 'Independent pros',     desc: 'Doctors, lawyers, accountants — AI applied with GDPR care.' },
    { Icon: GraduationCap, title: 'Families & seniors', desc: 'Sessions tailored to protect parents, grandparents and teens online.' },
  ];

  const faqs: FAQItem[] = isPT ? [
    {
      question: 'A formação é mesmo para pessoas sem conhecimentos técnicos?',
      answer: 'Sim, totalmente. Adaptamos a linguagem ao público. Na Higiene Digital partimos do princípio que o participante usa o telemóvel e o email — nada mais. Nas Formações IA mostramos as ferramentas em ecrã partilhado e damos exemplos do dia-a-dia. Sem código, sem termos técnicos.',
    },
    {
      question: 'Online ou presencial?',
      answer: 'Ambos. Online via Zoom/Meet funciona muito bem para equipas distribuídas e é mais barato. Presencial é melhor para grupos pequenos e quando há muitas dúvidas. Híbrido também é possível. Recomendamos depois da chamada gratuita.',
    },
    {
      question: 'Quanto custa exatamente?',
      answer: 'Higiene Digital começa em €250 (sessão de 2h online, até 20 pessoas). Formações IA começam em €350 (sessão de 3h online, até 15 pessoas). Versões de 4h/6h e pacotes para várias sessões são mais económicos por pessoa. Orçamento final após confirmarmos formato, duração e nº de participantes.',
    },
    {
      question: 'Posso comprar as duas formações em pacote?',
      answer: 'Sim, e recomendamos. Há um pacote combinado "AI + Hygiene" com desconto. As duas matérias complementam-se: usar IA bem sem comprometer dados sensíveis exige saber o básico de cibersegurança.',
    },
    {
      question: 'Recebemos materiais para consultar depois?',
      answer: 'Sim. Todos os participantes recebem um guia em PDF com os principais pontos, checklists práticas e links úteis. As gravações da sessão online também ficam disponíveis durante 60 dias.',
    },
    {
      question: 'Têm certificado para a equipa?',
      answer: 'Sim. No final da formação emitimos um certificado nominal de participação. Não é uma certificação oficial CNCS/ENISA, mas é útil para registos internos de formação contínua.',
    },
    {
      question: 'Posso usar IFRRU ou financiamento para a formação?',
      answer: 'Estas formações não são certificadas DGERT, por isso não são elegíveis para IFRRU ou financiamento público. Em compensação, são muito mais práticas, focadas e baratas que alternativas certificadas. Para necessidade de financiamento podemos encaminhar para parceiros DGERT.',
    },
  ] : [
    {
      question: 'Is the training really for non-technical people?',
      answer: 'Yes, fully. We adapt the language to the audience. For Digital Hygiene we assume the participant uses a phone and email — nothing more. For AI Training we demo tools on shared screen with everyday examples. No code, no jargon.',
    },
    {
      question: 'Online or in-person?',
      answer: 'Both. Online via Zoom/Meet works great for distributed teams and costs less. In-person is better for small groups with many questions. Hybrid is also possible — we recommend after the free intro call.',
    },
    {
      question: 'How much does it cost?',
      answer: 'Digital Hygiene starts at €250 (2h online session, up to 20 people). AI Training starts at €350 (3h online, up to 15 people). 4h/6h versions and multi-session packages are cheaper per person. Final quote after confirming format, duration and headcount.',
    },
    {
      question: 'Can I bundle both workshops?',
      answer: 'Yes, and we recommend it. There\'s a combined "AI + Hygiene" bundle with a discount. The two topics complement each other: using AI well without compromising sensitive data requires knowing cyber basics.',
    },
    {
      question: 'Do we get materials to keep?',
      answer: 'Yes. Every participant receives a PDF guide with key points, practical checklists and useful links. Online session recordings are available for 60 days.',
    },
    {
      question: 'Do you issue certificates?',
      answer: 'Yes. After the workshop we issue a named certificate of participation. It is not an official CNCS/ENISA certification, but is useful for internal continuous training records.',
    },
    {
      question: 'Are these eligible for public training funding?',
      answer: 'These workshops are not DGERT-certified, so they are not eligible for IFRRU or public funding. In return they are much more practical, focused and affordable than certified alternatives. If you need funded training we can refer you to DGERT partners.',
    },
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: 'Spawncore Studio',
    url: 'https://spawncore.com/services/training',
    description,
    provider: { '@type': 'Organization', name: 'Spawncore', url: 'https://spawncore.com' },
    areaServed: 'PT',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: isPT ? 'Catálogo de Formações' : 'Training Catalog',
      itemListElement: modules.map(m => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Course',
          name: m.title,
          description: m.description,
          provider: { '@type': 'Organization', name: 'Spawncore' },
        },
      })),
    },
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={isPT ? 'pt' : 'en'} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={isPT
          ? 'formação IA Portugal, workshop inteligência artificial, formação ChatGPT, formação cibersegurança utilizador, higiene digital, formação phishing, segurança online empresas, formação IA pequenas empresas, prompt engineering, cibersegurança para PME'
          : 'AI training Portugal, ChatGPT workshop, AI workshop small business, cybersecurity training users, digital hygiene, phishing training, online safety SMB, applied AI workshops, prompt engineering, cyber basics'} />
        <link rel="canonical" href="https://spawncore.com/services/training" />
        <link rel="alternate" hrefLang="pt-PT" href="https://spawncore.com/services/training" />
        <link rel="alternate" hrefLang="en" href="https://spawncore.com/services/training" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spawncore.com/services/training" />
        <meta property="og:image" content="https://spawncore.com/lovable-uploads/c121560e-1be0-4632-8106-cdadaf79cf04.png" />
        <meta property="og:locale" content={isPT ? 'pt_PT' : 'en_US'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <SafeJsonLd data={serviceSchema} />

      <header>
        <Navigation />
      </header>

      <ServiceBreadcrumb label={t('nav.training')} url="https://spawncore.com/services/training" />

      <main>
        {/* Hero */}
        <section className="relative pt-12 pb-20 md:pt-20 md:pb-28 overflow-hidden">
          <div className="pointer-events-none absolute inset-0 mesh-bg opacity-60" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />

          <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <span className="pill-tag mb-8">
              <GraduationCap className="h-3 w-3" />
              {isPT ? 'Workshops práticos · Sem jargão' : 'Practical workshops · No jargon'}
            </span>
            <h1 className="font-display text-foreground text-[clamp(2.5rem,7vw,5.5rem)] leading-[0.96] tracking-tight">
              {isPT ? 'Equipas que ' : 'Teams that '}
              <span style={{ color: 'hsl(var(--lime))' }} className="italic font-display">
                {isPT ? 'dominam IA' : 'master AI'}
              </span>
              {isPT ? ' e ' : ' and '}
              <span className="italic font-display text-primary">
                {isPT ? 'sabem defender-se' : 'stay safe online'}
              </span>
              .
            </h1>
            <p className="mt-7 text-lg md:text-2xl text-muted-foreground max-w-2xl leading-relaxed">
              {isPT
                ? 'Duas formações curtas e práticas, desenhadas para pessoas comuns. Sem código. Sem teoria a mais. Saem da sessão a usar — e a proteger-se — desde já.'
                : 'Two short, hands-on workshops, built for everyday people. No code. No fluff. Leave the session already using AI — and already safer online.'}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="group h-14 px-7 rounded-full bg-primary text-primary-foreground hover:bg-primary-light shadow-glow border border-primary-light/30 text-base w-full sm:w-auto"
                >
                  <GraduationCap className="mr-2 h-4 w-4" />
                  {isPT ? 'Marcar uma formação' : 'Book a workshop'}
                  <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <a href="#modules" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-7 rounded-full bg-card/40 border-border hover:bg-card text-base w-full sm:w-auto"
                >
                  {isPT ? 'Ver os módulos' : 'See modules'}
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Modules — the two big cards */}
        <section id="modules" className="relative py-20 md:py-28 bg-secondary/40 border-y border-border/60">
          <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-12 gap-6 mb-14 md:mb-16">
              <div className="col-span-12 md:col-span-7">
                <span className="eyebrow">— {isPT ? 'Os módulos' : 'The modules'}</span>
                <h2 className="mt-4 font-display text-foreground text-[clamp(2rem,4.2vw,3.5rem)] leading-[1.04] tracking-tight">
                  {isPT ? 'Duas formações que se complementam.' : 'Two workshops that complement each other.'}
                </h2>
              </div>
              <p className="col-span-12 md:col-span-4 md:col-start-9 self-end text-muted-foreground text-base md:text-lg leading-relaxed">
                {isPT
                  ? 'Pode fazer só uma. Mas em conjunto é mais barato — e a segurança importa quando se começa a usar IA.'
                  : 'You can take just one. But together it costs less — and security matters once you start using AI.'}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
              {modules.map((m, idx) => {
                const isLime = m.accent === 'lime';
                return (
                  <article
                    key={m.id}
                    className={[
                      'group relative flex flex-col overflow-hidden rounded-2xl border p-8 md:p-10 transition-all duration-500 hover:-translate-y-1',
                      isLime
                        ? 'border-lime/30 bg-card shimmer-border'
                        : 'border-border bg-card hover:border-primary/40',
                    ].join(' ')}
                  >
                    <div
                      className={[
                        'pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full blur-3xl opacity-50 transition-opacity duration-700',
                        isLime ? 'bg-lime/[0.10]' : 'bg-primary/[0.10]',
                      ].join(' ')}
                      aria-hidden="true"
                    />

                    <header className="relative z-10 flex items-start justify-between mb-8">
                      <span className={isLime ? 'pill-tag-lime' : 'pill-tag'}>
                        <m.Icon className="h-3 w-3" />
                        {m.tag}
                      </span>
                      <span className="font-mono-tag text-xs uppercase tracking-[0.32em] text-muted-foreground/70">
                        / 0{idx + 1}
                      </span>
                    </header>

                    <div className="relative z-10">
                      <h3 className="font-display text-foreground text-4xl md:text-5xl leading-[1.05] tracking-tight mb-4">
                        {m.title}
                      </h3>
                      <p className={[
                        'font-display italic text-xl md:text-2xl mb-5',
                        isLime ? 'text-lime/90' : 'text-primary-light',
                      ].join(' ')}>
                        {m.tagline}
                      </p>
                      <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-8">
                        {m.description}
                      </p>

                      <div className="mb-8">
                        <div className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-4">
                          / {isPT ? 'o que está incluído' : 'what\'s covered'}
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                          {m.coverage.map((point, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm text-foreground/85 leading-relaxed">
                              <CheckCircle2
                                className={[
                                  'h-4 w-4 mt-0.5 flex-shrink-0',
                                  isLime ? 'text-lime' : 'text-primary',
                                ].join(' ')}
                                strokeWidth={2}
                              />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-4 mb-8 pb-8 border-b border-border/60">
                        <div className="flex items-start gap-3 flex-1">
                          <Clock className={`h-4 w-4 mt-1 flex-shrink-0 ${isLime ? 'text-lime' : 'text-primary'}`} />
                          <div>
                            <div className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-1">
                              Format
                            </div>
                            <div className="text-sm text-foreground/90">{m.format}</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 flex-1">
                          <Sparkles className={`h-4 w-4 mt-1 flex-shrink-0 ${isLime ? 'text-lime' : 'text-primary'}`} />
                          <div>
                            <div className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-1">
                              {isPT ? 'Preço' : 'Price'}
                            </div>
                            <div className="text-sm font-medium text-foreground">{m.price}</div>
                          </div>
                        </div>
                      </div>

                      <Link
                        to="/contact"
                        className={[
                          'group/btn inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300',
                          isLime
                            ? 'bg-lime text-lime-foreground hover:bg-lime/90 shadow-glow-lime'
                            : 'bg-primary text-primary-foreground hover:bg-primary-light shadow-glow',
                        ].join(' ')}
                      >
                        {isPT ? 'Marcar ' : 'Book '}{m.title}
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5" />
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* Combined bundle callout */}
            <div className="mt-10 rounded-2xl border border-lime/30 bg-lime/[0.04] p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-lime/40 bg-lime/[0.10] text-lime flex-shrink-0">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-xl text-foreground mb-1.5">
                  {isPT ? 'Pacote AI + Higiene Digital' : 'AI + Digital Hygiene Bundle'}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  {isPT
                    ? 'Faça as duas formações em conjunto e poupe 20%. Recomendado para equipas que vão começar a usar IA no trabalho.'
                    : 'Take both workshops together and save 20%. Recommended for teams about to start using AI at work.'}
                </p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full border border-lime/40 bg-lime/[0.08] hover:bg-lime hover:text-lime-foreground transition-all duration-300 px-5 py-3 text-sm font-medium text-lime"
              >
                {isPT ? 'Pedir orçamento' : 'Get quote'}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Target groups */}
        <section className="py-20 md:py-28 bg-background">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-14 max-w-3xl">
              <span className="eyebrow">— {isPT ? 'Para quem' : 'Who it\'s for'}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'Formações adaptadas ao seu público.' : 'Workshops adapted to your audience.'}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {targetGroups.map((g, idx) => (
                <article
                  key={g.title}
                  className="group rounded-xl border border-border/80 bg-card/60 p-6 hover:bg-card hover:border-foreground/30 transition-colors"
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-secondary text-foreground">
                      <g.Icon className="h-4 w-4" strokeWidth={1.6} />
                    </div>
                    <div className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70">
                      / 0{idx + 1}
                    </div>
                  </div>
                  <h3 className="font-display text-lg text-foreground mb-2 leading-tight">{g.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{g.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <ServiceFAQ
          eyebrow={isPT ? 'Perguntas frequentes · Formações' : 'FAQ · Workshops'}
          heading={isPT ? 'Antes de marcar a formação.' : 'Before you book the workshop.'}
          subtitle={isPT ? 'O que perguntam os RH e formadores.' : 'What HR teams and trainers ask.'}
          faqs={faqs}
        />

        {/* Final CTA */}
        <section className="py-20 md:py-28 bg-background relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 mesh-bg opacity-50" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />

          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <span className="pill-tag-lime mb-6">
              <Sparkles className="h-3 w-3" />
              {isPT ? 'Chamada gratuita · 30 min' : 'Free 30-min call'}
            </span>
            <h2 className="font-display text-foreground text-[clamp(2rem,5vw,4rem)] leading-[1.02] tracking-tight mb-6">
              {isPT ? 'Pronto para formar a sua equipa?' : 'Ready to train your team?'}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              {isPT
                ? 'Marque uma chamada gratuita. Avaliamos o nível da equipa, escolhemos os tópicos e fechamos data e formato.'
                : 'Book a free call. We assess the team\'s level, pick the topics and lock in date and format.'}
            </p>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="group h-14 px-8 rounded-full bg-primary text-primary-foreground hover:bg-primary-light shadow-glow border border-primary-light/30 text-base w-full sm:w-auto"
              >
                <GraduationCap className="mr-2 h-4 w-4" />
                {isPT ? 'Falar com o estúdio' : 'Talk to the studio'}
                <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Button>
            </Link>
          </div>
        </section>

        <ServiceCrossSell current="training" />
      </main>

      <Footer />
    </div>
  );
};

export default ServicesTraining;
